import { Button } from "@/components/ui/Button";
import { Modal } from "@/components/ui/Modal";
import { useProceedingMutations } from "../hooks";
import { apiErr } from "../utils";
import type { Proceeding } from "../types";
import { CaseOutcomeFields, useCaseOutcome } from "./CaseOutcomeFields";

/**
 * „Zamknij sprawę” z Korespondencji — ten sam środek (CaseOutcomeFields) co „Zakończ”
 * wniosku: rozstrzygnięcie, przy częściowym działki uzgodnione (sprawa WPW zaznacza je
 * sama z danych zlecenia), notatka. Zamknięcie kończy też złożone wnioski sprawy
 * (pakiet WPW nie — jego koniec to zwrot z terenu). Wariant po zarejestrowaniu
 * odpowiedzi urzędu podmienia tytuł, wstęp i etykiety przycisków.
 */
export function CloseProceedingModal({
  proceeding,
  connectionId,
  onClose,
  title,
  intro,
  cancelLabel = "Anuluj",
  confirmLabel = "Zamknij sprawę",
}: {
  proceeding: Proceeding;
  connectionId: string;
  onClose: () => void;
  title?: string;
  intro?: React.ReactNode;
  cancelLabel?: string;
  confirmLabel?: string;
}) {
  const m = useProceedingMutations(connectionId);
  const resolution = useCaseOutcome("positive");
  const packages = proceeding.official_applications.filter(
    (a) => a.status === "submitted" && a.application_type !== "wpw_pakiet",
  );

  return (
    <Modal open onClose={onClose} title={title ?? `Zamknij sprawę „${proceeding.type_name}”`} size="sm">
      <div className="space-y-3 text-sm">
        {intro ?? (
          <p>
            Sprawa „{proceeding.type_name}” zamknie się ze wskazanym rozstrzygnięciem.
            {packages.length > 0 && (
              <>
                {" "}Złożony wniosek <b>{packages.map((a) => a.number).join(", ")}</b> dostanie
                razem z nią status „Zakończony”.
              </>
            )}
          </p>
        )}
        <CaseOutcomeFields state={resolution} proceedingId={proceeding.id} scope={proceeding.scope} />
        {m.close.error && <p className="text-xs text-red-600">{apiErr(m.close.error)}</p>}
        <div className="flex justify-end gap-2 pt-1">
          <Button size="sm" variant="ghost" onClick={onClose} disabled={m.close.isPending}>
            {cancelLabel}
          </Button>
          <Button
            size="sm"
            loading={m.close.isPending}
            disabled={resolution.missingParcels}
            onClick={() => m.close.mutate({ id: proceeding.id, ...resolution.values() }, { onSuccess: onClose })}
          >
            {confirmLabel}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
